// Library methods
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

// Utilities
import { getBatteryIcon } from "../../../utils/deviceHelper";
import { getLocalDate } from "../../../utils/dateHelper";

// Assets
import leftControllerIconRed from "../../../assets/images/controllers/left_red.svg";
import leftControllerIconGreen from "../../../assets/images/controllers/left_green.svg";
import leftControllerIconGrey from "../../../assets/images/controllers/left_grey.svg";
import rightControllerIconGreen from "../../../assets/images/controllers/right_green.svg";
import rightControllerIconGrey from "../../../assets/images/controllers/right_grey.svg";
import rigthControllerIconRed from "../../../assets/images/controllers/right_red.svg";
import cloudSlashIcon from "../../../assets/images/controllers/cloud_grey.svg";
import { useLiveUpdateStatusContext } from "../../../contexts/LiveUpdateStatusContext";
import { useExamStatus } from "../../../hooks/useExamStatus";
import { DeviceAppVersionContext } from "../../../contexts/DeviceContext";
import { IconButton } from "@mui/material";
import {
  DiagonalArrowsIcon,
  LinkIcon,
  UnlinkIcon,
  VROutlineIcon,
} from "../../../assets/icons";

const DevicesCard = ({
  row,
  handleLinkDeviceClick,
  handleUnlinkDeviceClick,
  isExamInProgress,
  devicesWithExams,
  linkLoading,
  unlinkLoading,
  clinicTimezone,
}) => {
  const { t } = useTranslation();

  const { getExamStatus } = useExamStatus();
  const { actions } = useLiveUpdateStatusContext();
  const { handleViewLiveUpdateClick } = actions;
  const { setNewAppVersionWarningPopup } = useContext(DeviceAppVersionContext);

  const [isLinked, setIsLinked] = useState(false);
  const device = useMemo(() => {
    // Only devicesWithExams has the latest data
    const device = devicesWithExams?.find((item) => item.id === row.id);
    if (device) {
      setIsLinked(true);
      return device;
    }
    setIsLinked(false);
    return device || row;
  }, [devicesWithExams, row]);
  // getting the exam's patient
  const linkedExamsPatient = useMemo(() => {
    // To avoid using not updated row data (associatedExam)
    if (isLinked) {
      return `${device?.associatedExam?.exam?.patient?.firstName} ${device?.associatedExam?.exam?.patient?.lastName}`;
    }
    return "";
  }, [
    device?.associatedExam?.exam?.patient?.firstName,
    device?.associatedExam?.exam?.patient?.lastName,
    isLinked,
  ]);

  const isOutdated = useMemo(() => {
    if (device?.webserviceCompatible === false) {
      return true;
    }
    return false;
  }, [device?.webserviceCompatible]);

  const handleClickOutdated = (device) => {
    // console.log("outdated", device);
  };
  const convertedDeviceExpiryDate = useMemo(() => {
    const d = row?.expiryDate;
    if (d) {
      return getLocalDate(d, clinicTimezone);
    }
    return null;
  }, [row?.expiryDate, clinicTimezone]);

  // Extract version number with regular expression
  const appVersion = useMemo(() => {
    if (device?.applicationVersion) {
      const regex = /\d+(\.\d+)*/g;
      return `v${device.applicationVersion.match(regex)}`;
    }
    return null;
  }, [device?.applicationVersion]);

  const leftDisconnected = useMemo(
    () =>
      !device?.deviceStatus?.controllerBatteryL ||
      device?.deviceStatus?.controllerBatteryL === -1,
    [device?.deviceStatus?.controllerBatteryL]
  );
  const rightDisconnected = useMemo(
    () =>
      !device?.deviceStatus?.controllerBatteryR ||
      device?.deviceStatus?.controllerBatteryR === -1,
    [device?.deviceStatus?.controllerBatteryR]
  );

  const examStatus = useMemo(
    () => getExamStatus(device?.associatedExam?.exam, devicesWithExams),
    [device?.associatedExam?.exam, devicesWithExams, getExamStatus]
  );

  const shouldDisableLiveUpdate = useMemo(
    () =>
      !device?.associatedExam?.exam ||
      !device?.id ||
      !device?.deviceStatus?.stage?.stageType,
    [device?.associatedExam?.exam, device?.id, device?.deviceStatus?.stage]
  );

  useEffect(() => {
    if (isOutdated) setNewAppVersionWarningPopup(isOutdated);
  }, [isOutdated, setNewAppVersionWarningPopup]);

  return (
    <Card
      elevation={0}
      key={device?.id}
      sx={{
        position: "relative",
        borderRadius: "8px",
        backgroundColor: "#FFF",
        border: isLinked
          ? "2px solid #202338"
          : isOutdated
            ? "2px solid #87201A"
            : "2px solid #E2772E",
        p: "12px",
        minWidth: "280px",
        maxWidth: "280px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "8px",
        "@keyframes color-change-border": {
          "0%": {
            border: "2px solid #202338",
          },
          "50%": {
            border: "2px solid #22BE39",
          },
          "100%": {
            border: "2px solid #202338",
          },
        },
        animation:
          shouldDisableLiveUpdate || isOutdated || !isLinked
            ? "none"
            : "color-change-border 2s infinite",
      }}
    >
      {/* Card Header with Device Name and Live Update Icon */}
      <Grid display="flex" alignItems="center" justifyContent="space-between">
        <Grid display="flex" alignItems="center" gap="8px">
          <VROutlineIcon color="#000" />
          <Grid display="flex" flexDirection="column">
            <Tooltip title={device?.alias}>
              <Typography
                noWrap
                color="#000"
                fontWeight={600}
                maxWidth={isLinked ? "155px" : "200px"}
              >
                {device?.alias}
              </Typography>
            </Tooltip>

            {/* Version Number and Battery Icon */}
            <Grid
              display="flex"
              alignItems="center"
              gap="8px"
              sx={{ height: "14px", lineHeight: "14px" }}
            >
              <Typography fontSize="14px" color="#5B6D7A">
                {appVersion}
              </Typography>
              {device?.deviceStatus?.battery >= 0 && (
                <Box display="flex" alignItems="center" gap="4px">
                  {getBatteryIcon(device.deviceStatus.battery)}
                  <Typography fontSize="12px" color="#000">
                    {Math.floor(device.deviceStatus.battery * 100)}%
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Grid>
        {/* Live Update Icon */}
        <Box display={isOutdated || !isLinked ? "none" : "flex"}>
          <Tooltip title={t("tooltip_live_update_panel")}>
            <span>
              <IconButton
                sx={{
                  color: "black",
                  "&.Mui-disabled": {
                    color: "#5B6D7A",
                    opacity: "0.43",
                  },
                  "@keyframes color-change-icon": {
                    "0%": {
                      color: "#000",
                    },
                    "50%": {
                      color: "#22BE39",
                    },
                    "100%": {
                      color: "#000",
                    },
                  },
                  animation: shouldDisableLiveUpdate
                    ? "none"
                    : "color-change-icon 2s infinite",
                }}
                disabled={shouldDisableLiveUpdate}
                onClick={() => {
                  if (shouldDisableLiveUpdate) return;
                  handleViewLiveUpdateClick(device?.id);
                }}
              >
                <DiagonalArrowsIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Grid>
      {/* Controller Icons and Patient Info */}
      <Grid display="flex" justifyContent="space-between">
        <Grid container item width="80px" display="flex" alignItems="center">
          <Tooltip
            title={`${t("word_left")} : ${
              leftDisconnected || device?.deviceStatus?.stage?.stageType == null
                ? t("word_offline")
                : `${device?.deviceStatus?.controllerBatteryL * 100}%`
            }`}
          >
            <Grid
              container
              item
              xs={6}
              display={"flex"}
              flexDirection={"column"}
            >
              <Grid
                item
                xs={12}
                display={"flex"}
                justifyContent={"center"}
                alignItems={
                  !leftDisconnected &&
                  device?.deviceStatus?.stage?.stageType != null
                    ? "flex-end"
                    : "center"
                }
              >
                <img
                  src={cloudSlashIcon}
                  alt="offline"
                  width="16px"
                  style={{
                    visibility:
                      leftDisconnected ||
                      device?.deviceStatus?.stage?.stageType == null
                        ? "visible"
                        : "hidden",
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                display={"flex"}
                justifyContent={"center"}
                alignItems={
                  !leftDisconnected &&
                  device?.deviceStatus?.stage?.stageType != null
                    ? "flex-end"
                    : "center"
                }
              >
                <img
                  src={
                    leftDisconnected ||
                    device?.deviceStatus?.stage?.stageType == null
                      ? leftControllerIconGrey
                      : device?.deviceStatus?.controllerBatteryL <= 0.2
                        ? leftControllerIconRed
                        : leftControllerIconGreen
                  }
                  alt="leftController"
                  width="20px"
                />
              </Grid>
            </Grid>
          </Tooltip>

          <Tooltip
            title={`${t("word_right")} : ${
              rightDisconnected ||
              device?.deviceStatus?.stage?.stageType == null
                ? t("word_offline")
                : `${device?.deviceStatus?.controllerBatteryR * 100}%`
            }`}
          >
            <Grid
              container
              item
              xs={6}
              display={"flex"}
              flexDirection={"column"}
            >
              <Grid
                item
                xs={12}
                display={"flex"}
                justifyContent={"center"}
                alignItems={
                  !rightDisconnected &&
                  device?.deviceStatus?.stage?.stageType != null
                    ? "flex-end"
                    : "center"
                }
              >
                <img
                  src={cloudSlashIcon}
                  alt="offline"
                  width="16px"
                  style={{
                    visibility:
                      rightDisconnected ||
                      device?.deviceStatus?.stage?.stageType == null
                        ? "visible"
                        : "hidden",
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                display={"flex"}
                justifyContent={"center"}
                alignItems={
                  !rightDisconnected &&
                  device?.deviceStatus?.stage?.stageType != null
                    ? "flex-end"
                    : "center"
                }
              >
                <img
                  src={
                    rightDisconnected ||
                    device?.deviceStatus?.stage?.stageType == null
                      ? rightControllerIconGrey
                      : device?.deviceStatus?.controllerBatteryR <= 0.2
                        ? rigthControllerIconRed
                        : rightControllerIconGreen
                  }
                  alt="rightController"
                  width="20px"
                />
              </Grid>
            </Grid>
          </Tooltip>
        </Grid>

        {/* Patient Name and Device Status */}
        <Grid display="flex" flexDirection="column" textAlign="right">
          <Typography
            fontSize="14px"
            fontWeight={600}
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "150px",
              color: shouldDisableLiveUpdate ? "#5B6D7A" : "#000",
            }}
          >
            {isLinked ? linkedExamsPatient : ""}
          </Typography>
          <Typography
            fontSize="14px"
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "150px",
              color: shouldDisableLiveUpdate ? "#5B6D7A" : "#000",
            }}
          >
            {isLinked && examStatus?.status ? examStatus?.status : ""}
          </Typography>
        </Grid>
      </Grid>

      {/* Device expiry text and Online/Offline status */}
      <Grid
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
      >
        <Grid display="flex" flexDirection="column">
          <Typography fontSize="10px" color="#5B6D7A">
            {`${t("device_card_expiry_date")} : ${convertedDeviceExpiryDate ?? "----"}`}
          </Typography>
          <Typography fontSize="10px" color="#5B6D7A">
            <span>{t("device_universalDeviceId")}</span>
            {": "}
            <span style={{ whiteSpace: "nowrap" }}>
              {device?.universalDeviceId ?? "----"}
            </span>
          </Typography>
        </Grid>
        <Grid item display="flex" alignItems="flex-end" minWidth="fit-content">
          {device?.deviceStatus?.stage?.stageType != null ? (
            <Box display="flex" alignItems="center" gap="4px">
              <Box
                backgroundColor="#22BE39"
                borderRadius="100%"
                height="10px"
                width="10px"
              />
              <Typography fontSize="14px" fontWeight={600} color="#22BE39">
                {t("word_online")}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap="4px">
              <Box
                backgroundColor="#5B6D7A70"
                borderRadius="100%"
                height="10px"
                width="10px"
              />
              <Typography fontSize="14px" fontWeight={600} color="#5B6D7A70">
                {t("word_offline")}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Link/Unlink/Outdated button */}
      <Grid item display="flex">
        {isLinked ? (
          <Button
            variant="outlined"
            sx={{
              textTransform: "capitalize",
              height: "40px",
              width: "100%",
              fontSize: "16px",
              backgroundColor: "#202338",
              color: "#FFF",
              border: "none",
              gap: "8px",
              "&:hover": {
                backgroundColor: "#0C0F24",
                border: "none",
              },
            }}
            disabled={isExamInProgress || unlinkLoading}
            onClick={(event) => {
              event.stopPropagation();
              handleUnlinkDeviceClick(device);
            }}
          >
            {t("unlink")}
            <UnlinkIcon color="#FFF" />
          </Button>
        ) : isOutdated ? (
          <Button
            variant="outlined"
            sx={{
              textTransform: "capitalize",
              height: "40px",
              width: "100%",
              fontSize: "16px",
              backgroundColor: "#87201A",
              color: "#FFF",
              border: "none",
              "&:disabled": {
                border: "none",
                backgroundColor: "#87201A",
                color: "#FFF",
              },
            }}
            disabled
            onClick={() => handleClickOutdated(row)}
            aria-label="outdated-button"
          >
            {t("button_outdated")}
          </Button>
        ) : (
          <Button
            variant="outlined"
            sx={{
              textTransform: "capitalize",
              height: "40px",
              width: "100%",
              fontSize: "16px",
              backgroundColor: "#E2772E",
              color: "#FFF",
              border: "none",
              gap: "8px",
              "&:hover": {
                backgroundColor: "#CE631A",
                color: "#FFF",
                border: "none",
              },
            }}
            disabled={linkLoading || isOutdated}
            onClick={() => handleLinkDeviceClick(device)}
            aria-label="link-button"
          >
            {t("button_link")}
            <LinkIcon color="#FFF" />
          </Button>
        )}
      </Grid>
    </Card>
  );
};

export default DevicesCard;
